import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useV3Api } from '../api/useV3Api';
import { errorText } from '../api/errorText';
import ColumnHeads from '../components/drift/ColumnHeads';
import FindingView from '../components/drift/FindingView';
import PageHeader from '../components/PageHeader';
import { usePlans } from '../drift/usePlans';
import DeleteSelected from '../components/drift/DeleteSelected';
import { CAPABILITIES, SEVERITIES, STATUSES, actionText, evaluateText, gapText, severityClass, whenText } from '../drift/findingModel';
import { useTableView } from '../drift/useTableView';
import { useV3Account } from '../useV3Account';

const ROUTE = 'v3/findings';
const SPAN = 11;                                   // the caret, the checkbox and the nine columns
const STATES = ['open', 'resolved'];
const OPEN_ONLY = { state: 'open' };               // the State filter starts on open: resolved findings are one choice away
const stateClass = (state) => (state === 'resolved' ? 'pill-green' : 'pill-amber');

// The columns: what each shows, and what it sorts and filters by. Severity sorts worst first.
const COLUMNS = [
  { key: 'severity', label: 'Severity', text: (f) => f.severity, sort: (f) => SEVERITIES.indexOf(f.severity), options: true, order: SEVERITIES },
  { key: 'device', label: 'Device', text: (f) => f.device },
  { key: 'capability', label: 'Capability', text: (f) => f.capability, options: true },
  { key: 'finding', label: 'Finding', text: (f) => `${gapText(f.gap)} ${f.subjectName || ''}`.trim() },
  { key: 'expected', label: 'Expected', text: (f) => f.expected },
  { key: 'observed', label: 'Observed', text: (f) => f.observed },
  { key: 'action', label: 'Action', text: (f) => actionText(f), options: true },
  { key: 'state', label: 'State', text: (f) => f.state, options: true, order: STATES },
  { key: 'seen', label: 'Last seen', text: (f) => whenText(f.lastSeenUtc), sort: (f) => f.lastSeenUtc },
];

// What is wrong on a customer's machines, as of the last evaluation: one row for each finding, worst first. A row opens in place to what was
// expected and observed, which policy decided and what would fix it. "Evaluate" judges the account again (POST v3/evaluate) and keeps
// the result; nothing is evaluated on a schedule yet, so the page says when each finding was last seen.
export default function FindingsPage() {
  const { get, post, del } = useV3Api();
  const { accounts, account, choose, loading: accountsLoading, error: accountsError } = useV3Account();
  const [address] = useSearchParams();
  // A dashboard tile opens this page already filtered: the address names a capability, severity or status (no controls for them: the grid filters)
  const capability = address.get('capability');
  const severity = address.get('severity');
  const status = address.get('status');
  const filters = useMemo(() => ({ capability: CAPABILITIES.includes(capability) ? capability : '', severity: SEVERITIES.includes(severity) ? severity : '', status: STATUSES.includes(status) ? status : '' }),
    [capability, severity, status]);
  const [list, setList] = useState({ loading: true, items: [], error: '' });
  const [reloads, setReloads] = useState(0);
  const [open, setOpen] = useState('');
  const [picked, setPicked] = useState({});                  // finding id -> true: the findings ticked for Create actions or Delete
  const [running, setRunning] = useState(false);
  const [kind, setKind] = useState('');                     // the type Evaluate judges: '' is every type
  const [notice, setNotice] = useState('');
  const [problem, setProblem] = useState('');
  const code = account && account.code;
  const plans = usePlans(code, { load: false });         // only to create actions for the findings picked: nothing is read from the actions

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    setList((l) => ({ ...l, loading: true }));
    get(ROUTE, { account: code, capability: filters.capability, severity: filters.severity, status: filters.status, state: 'all' })
      .then((body) => { if (!cancelled) setList({ loading: false, items: (body && body.items) || [], error: '' }); })
      .catch((ex) => { if (!cancelled) setList({ loading: false, items: [], error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get, code, filters, reloads]);

  // Unlisted software (a program no policy names) is not shown: it is most of the findings and mostly not for action.
  const shown = useMemo(() => list.items.filter((f) => f.gap !== 'unlisted'), [list.items]);
  const view = useTableView(shown, COLUMNS, null, OPEN_ONLY);
  const rows = open ? shown.filter((f) => f.id === open) : view.rows;           // an open finding stays on screen whatever the column filters say
  const chosen = view.rows.filter((f) => picked[f.id]);                              // only what the filters show is acted on, whatever else was ticked
  const allChosen = view.rows.length > 0 && chosen.length === view.rows.length;
  const toggleAll = () => setPicked((prev) => ({ ...prev, ...Object.fromEntries(view.rows.map((f) => [f.id, !allChosen])) }));

  const changeAccount = (value) => {
    choose(value);
    setOpen('');
    setPicked({});
    setNotice('');
    setProblem('');
  };

  // Evaluate judges the account and keeps the findings. Creating actions is a separate step, on the findings picked.
  const evaluate = async () => {
    setRunning(true);
    setNotice('');
    setProblem('');
    try {
      const result = await post('v3/evaluate', kind ? { account: code, capability: kind } : { account: code });
      setNotice(evaluateText(result));
      setOpen('');
      setReloads((n) => n + 1);
    } catch (ex) {
      setProblem(errorText(ex));
    } finally {
      setRunning(false);
    }
  };

  const createActions = async () => {
    await plans.proposeMany(chosen);
    setPicked({});
  };

  // Delete the findings picked: one call each (DELETE v3/findings, as Remove-cmaV3Finding does), so one refusal does not stop the others.
  const removeFindings = async () => {
    setRunning(true);
    setNotice('');
    setProblem('');
    let removed = 0;
    const refused = [];
    for (const f of chosen) {
      try {
        await del(ROUTE, { account: code, id: f.id });
        removed += 1;
      } catch (ex) {
        refused.push(`${f.subjectName || f.subject} on ${f.device}: ${errorText(ex)}`);
      }
    }
    setNotice(removed ? `Deleted ${removed} finding${removed === 1 ? '' : 's'}. One that is still true comes back at the next evaluation.` : '');
    setProblem(refused.length ? `${refused.length} not deleted. ${refused.slice(0, 3).join(' ')}${refused.length > 3 ? ' ...' : ''}` : '');
    setPicked({});
    setOpen('');
    setReloads((n) => n + 1);
    setRunning(false);
  };

  return (
    <>
      <PageHeader
        title="Findings"
        bear="bandal"
        subtitle="What is wrong on a customer's machines, as of the last evaluation. Click a finding to see what was expected, what was found and which policy decided."
      />

      {accountsError && <div className="field-error" role="alert">{accountsError}</div>}
      {notice && <div className="notice" role="status">{notice}</div>}
      {problem && <div className="field-error" role="alert">{problem}</div>}
      {plans.message.notice && <div className="notice" role="status">{plans.message.notice}</div>}
      {plans.message.problem && <div className="field-error" role="alert">{plans.message.problem}</div>}

      <div className="card">
        <div className="filter-bar toolbar">
          <div className="field inline">
            <label htmlFor="v3-account">Account</label>
            <select id="v3-account" value={code || ''} disabled={accountsLoading || accounts.length === 0} onChange={(e) => changeAccount(e.target.value)}>
              {accounts.map((a) => <option key={a.code} value={a.code}>{`${a.code} - ${a.name}`}</option>)}
            </select>
          </div>
          <div className="evaluate-bar">
            <select aria-label="Type to evaluate" value={kind} disabled={running} onChange={(e) => setKind(e.target.value)}>
              <option value="">All types</option>
              {CAPABILITIES.map((c) => <option key={c} value={c}>{c === 'cve' ? 'CVE' : c[0].toUpperCase() + c.slice(1)}</option>)}
            </select>
            <button type="button" className="btn btn-primary btn-sm" disabled={!code || running} onClick={evaluate}>{running ? 'Working...' : kind ? `Evaluate ${kind === 'cve' ? 'CVE' : kind}` : 'Evaluate'}</button>
          </div>
          <div className="bulk-bar" role="group" aria-label="Selected findings">
            <span className="sub">{chosen.length} selected of {view.rows.length} shown</span>
            <button type="button" className="btn btn-primary btn-sm" disabled={chosen.length === 0 || running || !!plans.busy} onClick={createActions}>Create actions ({chosen.length})</button>
            <DeleteSelected count={chosen.length} busy={running || !!plans.busy} noun="finding" onDelete={removeFindings} />
          </div>
        </div>

        {list.loading && <div className="empty">Loading...</div>}
        {list.error && <div className="card-body"><div className="field-error" role="alert">{list.error}</div></div>}
        {!list.loading && !list.error && shown.length === 0 && <div className="empty">No findings. Run "Evaluate" to judge this account.</div>}
        {shown.length > 0 && (
          <div className={`table-wrap${open ? ' has-open' : ''}`}>
            <table className="open-rows" aria-label="Findings">
              <thead>
                <ColumnHeads view={view} lead={1} select={{ checked: allChosen, indeterminate: chosen.length > 0 && !allChosen, onChange: toggleAll, label: 'Select all shown' }} />
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={SPAN} className="empty">No findings match the column filters. <button type="button" className="btn btn-sm" onClick={view.clear}>Clear filters</button></td></tr>
                )}
                {rows.map((f) => {
                  const isOpen = open === f.id;
                  const toggle = () => setOpen(isOpen ? '' : f.id);
                  return (
                    <React.Fragment key={f.id}>
                      <tr className={`row${isOpen ? ' open' : ''}`} data-testid={`finding-${f.id}`} aria-expanded={isOpen} tabIndex={0} onClick={toggle}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(); } }}>
                        <td><span className="caret" aria-hidden="true">&#9656;</span></td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" aria-label={`Select ${f.subjectName || gapText(f.gap)} on ${f.device}`} checked={!!picked[f.id]}
                            onChange={(e) => setPicked((prev) => ({ ...prev, [f.id]: e.target.checked }))} />
                        </td>
                        <td><span className={`pill ${severityClass(f.severity)}`}>{f.severity}</span></td>
                        <td className="strong"><Link to={`/v3/devices?account=${encodeURIComponent(code)}&device=${encodeURIComponent(f.device || f.deviceId)}`} onClick={(e) => e.stopPropagation()}>{f.device}</Link></td>
                        <td>{f.capability}</td>
                        <td>{gapText(f.gap)}<div className="sub">{f.subjectName}</div></td>
                        <td className="wrap">{f.expected}</td>
                        <td className="wrap">{f.observed}</td>
                        <td>{actionText(f)}</td>
                        <td><span className={`pill ${stateClass(f.state)}`}>{f.state}</span></td>
                        <td className="sub nowrap">{whenText(f.lastSeenUtc)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="detail" data-testid={`detail-${f.id}`}>
                          <td colSpan={SPAN}><FindingView finding={f} account={code} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
